/**
 * Zod schemas + adapter for the OneSignal chat push payload (chat v2).
 *
 * Push notifications come only from OneSignal. Its foreground `notificationDisplay`
 * event carries the full push payload, including the message body, sender, and
 * channel context. This is the SAME payload shape legacy chat consumed via
 * `PushNotificationPayloadAdapter` (src/domain/chat/legacy/helpers.js:99-112);
 * the field paths here mirror that adapter.
 *
 * Schema-first: the runtime validator and the TS type are both derived from the
 * schema. Parsing is defensive (`safeParse`) so a malformed push never throws
 * inside the OneSignal SDK callback.
 */
import { z } from 'zod';
import { pipe, Option } from 'effect';

// Sender identity (Sendbird uses snake_case on the wire).
const SenderSchema = z
  .object({
    user_id: z.string().optional(),
    nickname: z.string().optional(),
  })
  .partial();

// The inner `payload` block. `data` is a JSON-encoded string that decodes to an
// object containing `companyId` (and, for confidential docs, a workflowType).
const InnerPayloadSchema = z
  .object({
    message: z.string().optional(),
    message_id: z.union([z.string(), z.number()]).optional(),
    created_at: z.union([z.string(), z.number()]).optional(),
    data: z.string().optional(),
  })
  .partial();

const ChannelSchema = z
  .object({
    channel_url: z.string().optional(),
    name: z.string().optional(),
  })
  .partial();

// The OneSignal `notificationDisplay` event object. We only model the `data`
// branch the chat push uses; unknown keys are ignored.
export const OneSignalChatPushSchema = z.object({
  data: z.object({
    channel: ChannelSchema.optional(),
    sender: SenderSchema.optional(),
    payload: InnerPayloadSchema.optional(),
  }),
});

export type OneSignalChatPush = z.infer<typeof OneSignalChatPushSchema>;

// Decoded shape of `data.payload.data` (a JSON string on the wire).
const InnerDataSchema = z
  .object({
    companyId: z.string().optional(),
    workflowType: z.string().optional(),
  })
  .partial();

// A normalized toast entry produced from a parsed push. `key` is used by the
// v2 toast middleware for dedup; the rest are rendered by the ChatToast
// component (which takes the ChatToastData subset).
export interface ChatToastEntry {
  key: string;
  channelUrl: string;
  channelName: string;
  documentId: string | null;
  companyId: string;
  workflowType: string | null;
  // Sender's userGUID — the push `sender.user_id` mirrors the Sendbird
  // Member.userId, which in chat v2 is the userGUID (NOT the login email). Carried
  // so the ChatToast can resolve the sender's avatar from the participant store,
  // and reused by the middleware for the own-message suppression check; may be an
  // empty string when the push omits it (the toast then falls back to the initial).
  senderUserGUID: string;
  senderName: string;
  messagePreview: string;
  label: string;
}

// Legacy rule: a channel name split on '#' with exactly 5 parts is a document
// channel; the documentId is the 3rd segment. Mirrors the document-id
// extraction helper in legacy/helpers.js:78-81.
const extractDocumentId = (name: string): string | null => {
  const parts = name.split('#');
  return parts.length === 5 ? parts[2] : null;
};

const parseInnerData = (raw: string | undefined): { companyId: string; workflowType: string | null } => {
  const parsed = pipe(
    Option.fromNullable(raw),
    Option.flatMap((s) => {
      try {
        return Option.some(InnerDataSchema.parse(JSON.parse(s)));
      } catch {
        return Option.none();
      }
    }),
  );
  return {
    companyId: pipe(parsed, Option.flatMapNullable((d) => d.companyId), Option.getOrElse(() => '')),
    workflowType: pipe(parsed, Option.flatMapNullable((d) => d.workflowType), Option.getOrElse(() => null)),
  };
};

/**
 * Parse an unknown OneSignal push event and, if it is a chat push (has channel
 * context), return a normalized toast entry. Returns null for non-chat or
 * unparseable payloads — never throws.
 *
 * `label` is set to the channel name and may be an empty string when the push
 * has no channel name; the ChatToast component applies the translated
 * "New message" fallback at render time (so the fallback is i18n-aware). A
 * toast is never dropped just because naming data is missing.
 */
export const adaptOneSignalChatPush = (event: unknown): ChatToastEntry | null => {
  const result = OneSignalChatPushSchema.safeParse(event);
  if (!result.success) return null;

  const { channel, sender, payload } = result.data.data;
  const channelUrl = channel?.channel_url ?? '';
  const channelName = channel?.name ?? '';

  // A chat push must at least identify its channel; otherwise it is not ours.
  if (!channelUrl) return null;

  const messageId = payload?.message_id != null ? String(payload.message_id) : '';
  const createdAt = payload?.created_at != null ? String(payload.created_at) : '';
  const messagePreview = payload?.message ?? '';
  const senderUserGUID = sender?.user_id ?? '';
  const senderName = sender?.nickname ?? '';
  const { companyId, workflowType } = parseInnerData(payload?.data);

  // Dedup key: channelUrl + message id, falling back to created_at, then the
  // preview, so redelivered pushes collapse to one toast.
  const key = `${channelUrl}:${messageId || createdAt || messagePreview}`;

  // `label` is the channel name; may be empty. The rendering component applies a
  // translated "New message" fallback so the fallback string is i18n-aware.
  const label = channelName;

  return {
    key,
    channelUrl,
    channelName,
    documentId: channelName ? extractDocumentId(channelName) : null,
    companyId,
    workflowType,
    senderUserGUID,
    senderName,
    messagePreview,
    label,
  };
};

// ---------------------------------------------------------------------------
// Azure Web PubSub `CHAT_UNREAD_UPDATE` event (chat v2 closed-drawer counter).
//
// The backend forwards Sendbird webhooks over PubSub. Verified payload shape:
//   { type, event: 'CHAT_UNREAD_UPDATE',
//     metadata: {
//       total_unread: number,                       // authoritative workspace total
//       data: { app, companyId, documentId, documentKey, type },  // changed channel
//       type: 'group_channel:message_send' | 'group_channel:message_read' | ...
//     } }
//
// All fields are modelled truthfully so the derived type matches the wire (and the
// `data`/`type` context is available to the future per-company/document wiring). Only
// `metadata.total_unread` is REQUIRED — it is the sole field the header total counter
// consumes, so an invalid/missing value is the only thing that should block an update.
// Every other field is optional: a malformed `documentId` (etc.) must never prevent the
// authoritative total from being applied. Unknown keys are ignored by default.
// ---------------------------------------------------------------------------
const ChatUnreadUpdateChannelDataSchema = z.object({
  app: z.string().optional(),
  companyId: z.string().optional(),
  documentId: z.string().optional(),
  documentKey: z.string().optional(),
  type: z.string().optional(),
});

export const ChatUnreadUpdateSchema = z.object({
  event: z.literal('CHAT_UNREAD_UPDATE'),
  metadata: z.object({
    total_unread: z.number(),
    type: z.string().optional(),
    data: ChatUnreadUpdateChannelDataSchema.optional(),
  }),
});

export type ChatUnreadUpdate = z.infer<typeof ChatUnreadUpdateSchema>;
